<template>
  <div
    @click="click"
    class="text-input"
    :class="{
      'text-input--focused': focused,
      'text-input--multiline': multiline,
      'text-input--inset': inset,
      'text-input--large': large,
      'text-input--huge': huge,
      'text-input--has-icon': icon,
      'text-input--copy-on-click': copyOnClick
    }"
  >
    <div class="text-input__container">
      <label class="text-input__label" v-if="label" :for="id">
        {{ label }}
      </label>
      <input
        v-if="!this.multiline"
        v-model="model"
        ref="input"
        class="text-input__input"
        type="text"
        :placeholder="placeholder"
        :id="id"
        @focus="onFocus"
        @blur="onBlur"
        @input="onInput"
        :readonly="readonly"
      />
      <textarea
        v-else
        v-model="model"
        ref="input"
        class="text-input__input"
        type="text"
        :placeholder="placeholder"
        :id="id"
        @focus="onFocus"
        @blur="onBlur"
        @input="onInput"
        :readonly="readonly"
        ></textarea>
        <FadeTransition>
            <span v-if="error" class="error">{{error}}</span>
        </FadeTransition>
    </div>
    <div v-if="icon" class="text-input-icon">
      <img class="text-input-icon__image" :src="icon" aria-hidden="true" />
    </div>

    <div v-if="showCopySuccess" class="text-input-copy-success">
      Copied!
    </div>
  </div>
</template>

<script>
import { v4 as uuid } from 'uuid'
import FadeTransition from '@/components/transitions/FadeTransition.vue'

/**
 * Updated version of <inputfield> & <inputarea> components.
 * We should use this for everything new, and move the old ones over to this.
 */
export default {
    name: "TextInput",
    components: {
        FadeTransition
    },

  props: {
    label: String,
    placeholder: String,

    multiline: Boolean,
    value: String,
    inset: Boolean,
    large: Boolean,
    huge: Boolean,
    focusOnMounted: Boolean,
    readonly: Boolean,
    icon: String,
    copyOnClick: Boolean,
    clickHandler: Function,
    error: String
  },

  data() {
    return {
      id: '',
      focused: false,
      showCopySuccess: false,
      showCopySuccessInterval: null
    };
  },

  mounted() {
    this.id = uuid();

    if (this.focusOnMounted) {
      this.focus();
    }
  },

  computed: {
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    }
  },
  methods: {
    click() {
      this.focus()
      if (this.copyOnClick) {
        this.$refs.input.select()
        this.$refs.input.setSelectionRange(0, 99999)

        navigator.clipboard.writeText(this.value)
        this.showCopySuccess = true

        if (this.showCopySuccessTimeout != null) clearTimeout(this.showCopySuccessTimeout)
        this.showCopySuccessTimeout = setTimeout(() => {
          this.showCopySuccess = false
          this.showCopySuccessTimeout = null
        }, 2000)

        if(this.clickHandler) this.clickHandler()
      }
    },
    focus() {
      this.$refs.input.focus()
    },
    onFocus() {
      this.focused = true
      this.$emit("onFocus")
    },
    onBlur() {
      this.focused = false
      this.$emit("onBlur")
    },
    onInput(e) {
      this.$emit('onInput', e)
    }
  }
};
</script>

<style lang="scss" scoped>
.error {
    color: $red;
    font-size: 14px;
}

.text-input {
  position: relative;
  font-size: 1rem;
  background-color: $grey-4;
  padding: 10px 15px 10px 15px;
  border-radius: 15px;
  cursor: text;
  transition: background-color 0.1s;
  display: flex;
  text-align: left;
  color: white;

  &:not(&--focused):not(&--multiline) {
    .text-input__input {
      // Show ellipsis when not focused
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  @include mobile-only {
    background-color: $grey-5;
  }

  &--copy-on-click {
    cursor: pointer;

    .text-input__input {
      pointer-events: none;
    }
  }

  &--inset {
    background-color: $grey-5;
  }

  &--focused {
    background-color: #4f4d4d;
  }

  &--large {
    padding-bottom: 30px;

    .text-input__input {
      font-size: 1.4rem;
      margin-top: 5px;
    }
  }

  &--multiline {
    .text-input__input {
      margin-top: 15px;
      height: 100px;
    }
  }

  &__label {
    margin-left: 2px;
    font-size: 0.8rem;
    font-weight: bold;
    display: block;
    color: $grey-1;
    pointer-events: none;
  }

  &__container {
    width: 100%;
    align-items: center;
  }

  &-icon {
    width: 60px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  &__input {
    font-size: 1rem;
    font-weight: bold;
    border-radius: 0;
    background: none;
    border: none;
    color: white;
    font-weight: bold;
    width: 100%;
    height: 1.5rem;
    margin: 0;

    &::placeholder {
      color: $grey-3;
    }
  }

  &--huge {
    background: none;
    padding: 0px;
    border-bottom: 2px solid white;
    border-radius: 0;
    padding-bottom: 10px;
    .text-input {
      &__input {
        font-size: 56px;
        height: auto;
        &::placeholder {
          color: white;
          font-weight: normal;
          font-style: italic;
        }
      }
    }

    @include mobile-only {
      .text-input {
        &__input {
          font-size: 24px;
        }
      }
    }
  }

  &-copy-success {
    position: absolute;
    top: 0;
    right: 0;
    background-color: $active-user-room;
    border-radius: 8px 15px 8px 8px;
    padding: 8px;

    animation: toast-float 2s ease-in-out;
    animation-fill-mode: forwards;
  }
}

@keyframes toast-float {
  0% {
    opacity: 0;
    transform: translateY(10%);
  }

  10%, 90% {
    opacity: 1;
    transform: translateY(0%);
  }

  100% {
    opacity: 0;
    transform: translateY(-50%);
  }
}
</style>
